"use client";

import { useCallback, useEffect, useRef } from "react";
import { useStore } from "zustand";
import { GradingFormSchema, gradingSchema } from "./gradingSchema";
import { useGrade } from "./useGrade";

const URL_PARAMS = [
  "passingGrade",
  "maxScore",
  "term",
  "linearPercentage",
  "pointsType",
] as const;

function parseURLParams(): Partial<GradingFormSchema> | null {
  if (typeof window === "undefined") return null;

  const params = new URLSearchParams(window.location.search);
  const hasAnyParam = URL_PARAMS.some((key) => params.has(key));

  if (!hasAnyParam) return null;

  const result: Record<string, string> = {};
  URL_PARAMS.forEach((key) => {
    const value = params.get(key);
    if (value) {
      result[key] = value;
    }
  });

  return result as Partial<GradingFormSchema>;
}

function buildURLParams(data: GradingFormSchema): string {
  const params = new URLSearchParams();
  URL_PARAMS.forEach((key) => {
    params.set(key, data[key]);
  });
  return params.toString();
}

export function useURLSync() {
  const { store, data, _hasHydrated } = useStore(useGrade, (state) => state);
  const isInitialMount = useRef(true);
  const hasAppliedURLParams = useRef(false);

  // On mount: read URL params and apply to store (only once)
  useEffect(() => {
    if (!_hasHydrated || hasAppliedURLParams.current) return;

    const urlParams = parseURLParams();
    if (urlParams) {
      const merged = { ...data, ...urlParams };
      const validated = gradingSchema.safeParse(merged);


      if (validated.success) {
        store(validated.data);
      }
    }
    hasAppliedURLParams.current = true;
  }, [_hasHydrated, data, store]);

  // On store change: update URL (after initial mount)
  useEffect(() => {
    if (!_hasHydrated) return;

    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    const newURL = `${window.location.pathname}?${buildURLParams(data)}`;
    window.history.replaceState({}, "", newURL);
  }, [data, _hasHydrated]);

  const copyShareLink = useCallback(async (): Promise<boolean> => {
    const url = `${window.location.origin}${window.location.pathname}?${buildURLParams(data)}`;
    try {
      await navigator.clipboard.writeText(url);
      return true;
    } catch {
      return false;
    }
  }, [data]);

  const getShareLink = useCallback((): string => {
    return `${window.location.origin}${window.location.pathname}?${buildURLParams(data)}`;
  }, [data]);

  return { copyShareLink, getShareLink, hasAppliedURLParams };
}
